import { useState } from "react";
import { API_BASE } from "../config";
import { toast } from "../ui/Toast";
import { track } from "../track";
import type { Words } from "./copy";

const TAGS = ["pose", "colour", "face", "add"] as const;
const TAG_KEY: Record<(typeof TAGS)[number], string> = {
  pose: "tagPose",
  colour: "tagColour",
  face: "tagFace",
  add: "tagAdd",
};

/** One tap, then words if they feel like it.
 *
 *  The old version was a blank textarea, which asks someone to compose a
 *  paragraph, often in a second language, to a stranger. Fifty-eight people
 *  looked at it and typed nothing, and a lead who hated the face was
 *  indistinguishable from one who never opened the link.
 *
 *  "Love it" is not only telemetry: having just said it is good makes acting on
 *  it a moment later more consistent. The order button that used to follow it
 *  in this card is gone: the bar at the bottom of the screen is the page's one
 *  order button, and two identical buttons in one view read as a mistake. The
 *  sheet still waits for a press of it: opening checkout at someone who only
 *  meant to say they liked it reads as a push. */
export default function Feedback({
  token,
  words: t,
  said,
  pieceId,
  onAnswered,
  onChangeSent,
}: {
  token: string;
  words: Words;
  /** What they already answered on an earlier visit, if anything. Someone who
   *  said "Love it" and came back to order finds the order button where they
   *  left it, rather than the question again. */
  said: "loved" | "issue" | null;
  /** The piece this card is under, on a page of several; the answer and any
   *  change are then about that piece alone. Left out, they are about the page,
   *  which is what a page of one and every older link mean. */
  pieceId?: string;
  /** They answered, and the server has it. For a page that shows one piece of
   *  several at a time: the card is made again when they come back to the
   *  piece, and starts from what the server said when the page was read, which
   *  does not know of an answer given since. */
  onAnswered?: (reaction: "loved" | "issue") => void;
  /** A change has been sent, and whether it is being fixed while they watch,
   *  which the page then shows on the drawing instead of "I'll redo it". */
  onChangeSent?: (live: boolean) => void;
}) {
  const [stage, setStage] = useState<"ask" | "issue" | "done">(said ? "done" : "ask");
  const [loved, setLoved] = useState(said === "loved");
  const [tags, setTags] = useState<string[]>([]);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  async function send(reaction: "loved" | "issue") {
    setSending(true);
    try {
      const response = await fetch(`${API_BASE}/api/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          reaction,
          tags,
          message: message.trim(),
          ...(pieceId ? { pieceId } : {}),
        }),
      });
      if (!response.ok) throw new Error(String(response.status));
      const answer = (await response.json().catch(() => ({}))) as { live?: boolean };
      setStage("done");
      setLoved(reaction === "loved");
      onAnswered?.(reaction);
      if (reaction === "loved") toast(t.t("loved"), "good");
      // Not "I'll redo it" to someone about to watch it be redone.
      else if (!answer.live) toast(t.t("changeDone"), "good", 5000);
      if (reaction === "issue") onChangeSent?.(!!answer.live);
    } catch {
      toast(t.t("sendFailed"), "bad");
    } finally {
      setSending(false);
    }
  }

  if (stage === "done") {
    // The thanks and the way back on one line, not a green box over a lone
    // centred button. Now that "Love it" is remembered across visits, "Alterar"
    // is the only way back to the change form for someone who has since
    // thought better.
    const thanks = (className: string) => (
      <div className={className}>
        <span className="feedback-said-text">
          <span className="feedback-said-tick" aria-hidden="true">✓</span>
          {loved ? t.t("loved") : t.t("changeDone")}
        </span>
        {loved && (
          <button
            type="button"
            className="btn btn-ghost btn-sm feedback-said-change"
            // A word beside the thanks, so the two fit on a phone's line;
            // read out as the whole question it answers.
            aria-label={t.t("notRight")}
            onClick={() => {
              track("feedback.issue");
              setStage("issue");
            }}
          >
            {t.t("changeShort")}
          </button>
        )}
      </div>
    );
    return thanks("card feedback-said");
  }

  if (stage === "issue") {
    return (
      <div className="card stack">
        <h2>{t.t("changeTitle")}</h2>
        <p className="small muted" style={{ margin: 0 }}>
          {t.t("changeHint")}
        </p>
        {/* Wrapping inside the card: the page-edge scroller sliced the last
            chip off at the card's border, where it read as clipped. */}
        <div className="row row-wrap" style={{ gap: 8 }}>
          {TAGS.map((tag) => (
            <button
              key={tag}
              className={`chip${tags.includes(tag) ? " chip-on" : ""}`}
              onClick={() =>
                setTags((current) =>
                  current.includes(tag)
                    ? current.filter((x) => x !== tag)
                    : [...current, tag]
                )
              }
            >
              {t.t(TAG_KEY[tag])}
            </button>
          ))}
        </div>
        <textarea
          className="field"
          value={message}
          maxLength={1000}
          placeholder={t.t("tellMore")}
          onChange={(e) => setMessage(e.target.value)}
        />
        {/* Said where the button is, so a Send that will not press is not a
            mystery: it wants one of the chips or a few words. */}
        {tags.length === 0 && message.trim().length < 2 && (
          <p className="tiny muted" style={{ margin: 0 }}>
            {t.t("changeNeed")}
          </p>
        )}
        <button
          className="btn btn-primary btn-block"
          disabled={sending || (tags.length === 0 && message.trim().length < 2)}
          onClick={() => void send("issue")}
        >
          {sending ? <span className="spin spin-on-red" /> : t.t("changeSend")}
        </button>
      </div>
    );
  }

  return (
    <div className="card stack">
      <h2>{t.t("ask")}</h2>
      <div className="row row-wrap row-even">
        <button
          className="btn btn-soft grow"
          disabled={sending}
          onClick={() => {
            track("feedback.loved");
            void send("loved");
          }}
        >
          {t.t("love")}
        </button>
        <button
          className="btn btn-quiet grow"
          onClick={() => {
            // Recorded before any chip is picked: the objection exists even if
            // they never finish telling us what it was.
            track("feedback.issue");
            setStage("issue");
          }}
        >
          {t.t("notRight")}
        </button>
      </div>
    </div>
  );
}
